import {useEffectExceptFirstRender} from '@wandb/weave/common/util/hooks';
import {Button} from '@wandb/weave/components';
import {Icon} from '@wandb/weave/components/Icon';
import React, {memo, ReactNode, useEffect, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Form, Popup} from 'semantic-ui-react';

import {getProjectInternalId} from '../RunSelector/util';
import * as S from './ProjectPicker.styles';
import {useProjectOptions} from './useProjectOptions';

interface ProjectPickerProps {
  entityName: string;
  projectName: string | undefined;
  triggerOverride?: ReactNode;
  requireSameEntity?: boolean;
  requireSameEntityReason?: string;
  onOpen?: () => void;
  onClose?: () => void;
  projectId?: string | undefined;
  setProject(entityName: string, projectName: string, id: string): void;
}
const decodeAndExtractId = (base64String: string) => {
  const decodedString = atob(base64String);
  const idMatch = decodedString.match(/ProjectInternalId:(\d+)/);
  return idMatch ? idMatch[1] : '';
};

export const ProjectPickerComp: React.FC<ProjectPickerProps> = ({
  entityName,
  onClose,
  onOpen,
  projectName,
  requireSameEntity,
  requireSameEntityReason,
  setProject,
  triggerOverride,
  projectId,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const {loading, projects} = useProjectOptions({
    entityName,
    queryString: searchQuery,
  });
  useEffectExceptFirstRender(() => {
    if (open) {
      onOpen?.();
    } else {
      onClose?.();
    }
    // eslint-disable-next-line
  }, [open]);

  let projectsWithCurrentProject = projects;
  const projectIdGraphQL = getProjectInternalId(projectId);
  let currentProjectIndex = projectsWithCurrentProject.findIndex(
    p =>
      p.internalId === projectIdGraphQL ||
      (p.entityName === entityName && p.name === projectName)
  );
  if (currentProjectIndex === -1 && projectName != null) {
    projectsWithCurrentProject = [
      {
        entityName,
        name: projectName,
        id: '',
        createdAt: '',
      },
      ...projectsWithCurrentProject,
    ];
    currentProjectIndex = 0;
  }
  const displayProjectName = projects[currentProjectIndex]?.name;
  const displayEntityName = projects[currentProjectIndex]?.entityName;

  const options = projectsWithCurrentProject.map((p, value) => {
    const disabled = requireSameEntity && p.entityName !== entityName;
    const showPopup = disabled && requireSameEntityReason;
    const text = `${p.entityName}/${p.name}`;
    return {
      text,
      value,
      disabled,
      content: showPopup ? (
        <Popup
          basic
          on="hover"
          size="mini"
          className="wb-table-action-popup"
          position="bottom right"
          content={
            <span className="hint-text small">{requireSameEntityReason}</span>
          }
          trigger={
            <S.DisabledDropdownOption>
              <span>{text}</span>
              <Icon name="lock-closed" />
            </S.DisabledDropdownOption>
          }
        />
      ) : (
        <span>{text}</span>
      ),
    };
  });
  // sort disabled options to the bottom of the list
  options.sort(a => (a.disabled ? 1 : -1));
  useEffect(() => {
    if (
      !loading &&
      currentProjectIndex === -1 &&
      projectsWithCurrentProject.length > 0
    ) {
      setProject(
        projectsWithCurrentProject[0].entityName,
        projectsWithCurrentProject[0].name,
        decodeAndExtractId(projectsWithCurrentProject[0].internalId ?? '')
      );
    }
  }, [loading, currentProjectIndex, projectsWithCurrentProject, setProject]);

  return (
    <Popup
      basic
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      className="wb-table-action-popup"
      on="click"
      position="bottom left"
      popperModifiers={{
        preventOverflow: {
          // prevent popper from erroneously constraining the popup to the
          // table header
          boundariesElement: 'viewport',
        },
      }}
      trigger={
        triggerOverride ? (
          triggerOverride
        ) : (
          <Button
            variant="ghost"
            icon="folder-project"
            aria-label="Open project picker"
            twWrapperStyles={{display: 'inline'}}
            className="mr-12">
            {displayEntityName && displayProjectName ? (
              <div className="flex">
                <div className="max-w-[5rem] truncate">{displayEntityName}</div>
                /
                <div className="max-w-[8rem] truncate">
                  {displayProjectName}
                </div>
              </div>
            ) : (
              ''
            )}
          </Button>
        )
      }
      content={
        <Form>
          <Form.Dropdown
            aria-label="Select project for run set"
            selection
            search
            searchQuery={searchQuery}
            onSearchChange={(e, {searchQuery: newSearchQuery}) =>
              setSearchQuery(newSearchQuery)
            }
            value={currentProjectIndex}
            options={options}
            onChange={(e, {value}) => {
              if (typeof value === 'number') {
                const proj = projectsWithCurrentProject[value];
                setProject(
                  proj.entityName,
                  proj.name,
                  decodeAndExtractId(proj.internalId ?? '')
                );
                setOpen(false);
              }
            }}
          />
        </Form>
      }
    />
  );
};

export const ProjectPicker = memo(ProjectPickerComp);
ProjectPicker.displayName = 'ProjectPicker';
